/// <amd-module name="Analytics/Shared/Utils/Culture" />
let currentCulture: ICultureInfo;
let invariantCulture: ICultureInfo;

/**
 * Culture-related settings
 */
export interface ICultureInfo {
    name: string;
    numberFormat: INumberFormatSettings;
    numberShortForm: INumberShortForm;
}

/**
 * Number Short form setting
 * it is the same internal class from the ClientCultureInfo.cs
 */
export interface INumberShortForm {
    QuantitySymbols: string[];
    NumberGroupSize: number;
    ThousandSymbol: string;
}

/**
 * Number formatting culture settings
 */
export interface INumberFormatSettings {
    CurrencyDecimalDigits: number;
    CurrencyDecimalSeparator: string;
    CurrencyGroupSizes: number[];
    NumberGroupSizes: number[];
    PercentGroupSizes: number[];
    CurrencyGroupSeparator: string;
    CurrencySymbol: string;
    NaNSymbol: string;
    CurrencyNegativePattern: number;
    NumberNegativePattern: number;
    PercentPositivePattern: number;
    PercentNegativePattern: number;
    NegativeInfinitySymbol: string;
    NegativeSign: string;
    NumberDecimalDigits: number;
    NumberDecimalSeparator: string;
    NumberGroupSeparator: string;
    CurrencyPositivePattern: number;
    PositiveInfinitySymbol: string;
    PositiveSign: string;
    PercentDecimalDigits: number;
    PercentDecimalSeparator: string;
    PercentGroupSeparator: string;
    PercentSymbol: string;
    PerMilleSymbol: string;
    NativeDigits: string[];
    DigitSubstitution: number;
}

/**
 * Get culture settings for the invariant culture
 */
export function getInvariantCulture() {
    if (!invariantCulture) {
        invariantCulture = {
            name: "en-US",
            numberFormat: {
                CurrencyDecimalDigits: 2,
                CurrencyDecimalSeparator: ".",
                CurrencyGroupSizes: [3],
                NumberGroupSizes: [3],
                PercentGroupSizes: [3],
                CurrencyGroupSeparator: ",",
                CurrencySymbol: "$",
                NaNSymbol: "NaN",
                CurrencyNegativePattern: 0,
                NumberNegativePattern: 1,
                PercentPositivePattern: 0,
                PercentNegativePattern: 0,
                NegativeInfinitySymbol: "-Infinity",
                NegativeSign: "-",
                NumberDecimalDigits: 2,
                NumberDecimalSeparator: ".",
                NumberGroupSeparator: ",",
                CurrencyPositivePattern: 0,
                PositiveInfinitySymbol: "Infinity",
                PositiveSign: "+",
                PercentDecimalDigits: 2,
                PercentDecimalSeparator: ".",
                PercentGroupSeparator: ",",
                PercentSymbol: "%",
                PerMilleSymbol: "\u2030",
                NativeDigits: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"],
                DigitSubstitution: 1
            },
            numberShortForm: {
                QuantitySymbols: ["K", "M", "B"],
                NumberGroupSize: 1000,
                ThousandSymbol: "K"
            }
        };
    }
    return invariantCulture;
}

/**
 * Get culture settings for the current user's preferred culture
 */
export function getCurrentCulture() {
    if (!currentCulture) {
        currentCulture = getInvariantCulture();
    }
    return currentCulture;
}

/**
 * Get the name of the current culture being used on this page
 */
export function getCurrentCultureName(): string {
    return getCurrentCulture().name;
}

/**
 * Get the number format settings for the current culture
 */
export function getNumberFormat(): INumberFormatSettings {
    return getCurrentCulture().numberFormat;
}

/**
 * Get the Number Short Form setting for the current culture
 */
export function getNumberShortForm(): INumberShortForm {
    return getCurrentCulture().numberShortForm;
}
